import { useState, useEffect } from "react"

import {
  ArrowBack,
  CheckCircle,
  Cancel,
  Receipt,
  Psychology,
} from '@mui/icons-material'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useParams, useNavigate, useLocation, Link } from "react-router-dom"

import { aiQcService, handleApiError, type AIQCReview, type AIQCResponse } from "../../api"
import { EnhancedReportDisplay } from "../ai/enhanced-report-display"

export function ReviewPage() {
  const { reviewId } = useParams<{ reviewId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const navigateBack = () => {
    if (location.key === "default") {
      navigate("/ai-qc/pending")
      return
    }
    navigate(-1)
  }

  const [review, setReview] = useState<AIQCReview | null>(null)
  const [aiResponse, setAiResponse] = useState<AIQCResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [aiResponseLoading, setAiResponseLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!reviewId) return

    const loadReviewData = async () => {
      try {
        // Get all reviews to find the specific one
        const allReviews = await aiQcService.getAllReviews()
        const foundReview = allReviews.find(r => r.id === reviewId)

        if (!foundReview) {
          throw new Error('Review not found')
        }

        setReview(foundReview)

        // Load actual AI response data
        setAiResponseLoading(true)
        try {
          const aiResponseData = await aiQcService.getAIResponse(foundReview.aiResponseId)
          setAiResponse(aiResponseData)
        } catch (aiError) {
          console.error('Failed to load AI response:', aiError)
          setAiResponse({
            hasRecommendations: false,
            message: 'Failed to load AI response data',
            recommendations: null,
          })
        } finally {
          setAiResponseLoading(false)
        }

      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setLoading(false)
      }
    }

    loadReviewData()
  }, [reviewId])

  const reloadReviewData = async () => {
    if (!reviewId) return

    try {
      const allReviews = await aiQcService.getAllReviews()
      const foundReview = allReviews.find(r => r.id === reviewId)
      if (foundReview) {
        setReview(foundReview)
      }
    } catch (err) {
      console.error('Failed to reload review data:', err)
    }
  }

  const handleApprove = async () => {
    if (!review) return

    setActionLoading(true)
    try {
      await aiQcService.approveReview(review.id)
      await reloadReviewData()
      setError("") // Clear any previous errors
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setActionLoading(false)
    }
  }

  const handleReject = async () => {
    if (!review) return

    setActionLoading(true)
    try {
      await aiQcService.rejectReview(review.id)
      await reloadReviewData()
      setError("") // Clear any previous errors
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'warning'
      case 'approved': return 'success'
      case 'rejected': return 'error'
      default: return 'default'
    }
  }

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) {
        return "Invalid date"
      }
      return `${date.toISOString().split('T')[0]} ${date.toTimeString().split(' ')[0]?.slice(0, 5)}`
    } catch {
      return "Invalid date"
    }
  }

  if (loading) {
    return (
      <Box sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!review || !aiResponse) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h6">Review not found</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            AI Report Review
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review ID: {review.id}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={navigateBack}
          >
            Back to Reviews
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            onClick={handleReject}
            disabled={actionLoading}
          >
            {actionLoading ? "Processing..." : "Reject"}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            onClick={handleApprove}
            disabled={actionLoading}
          >
            {actionLoading ? "Processing..." : "Approve"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Receipt />
              <Typography variant="h6" component="h2">
                Review Information
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={review.status.toUpperCase()}
                  color={getStatusColor(review.status)}
                  size="small"
                  variant="outlined"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">{formatDate(review.createdAt)}</Typography>
              </Grid>
              {review.session?.metadata?.labOrderId && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Order ID
                  </Typography>
                  <Link
                    to={`/orders/${review.session.metadata.labOrderId}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Typography 
                      variant="body2" 
                      color="primary" 
                      sx={{ fontFamily: 'monospace', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {review.session.metadata.labOrderId}
                    </Typography>
                  </Link>
                </Grid>
              )}
              {review.reviewedBy && (
                <Grid size={{ xs: 12, md: 8 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reviewed By
                  </Typography>
                  <Typography variant="body2">
                    {review.reviewedBy.firstName} {review.reviewedBy.lastName} ({review.reviewedBy.email})
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* AI Prompt Details Card */}
        {aiResponse?.metadata?.prompt && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Psychology />
                <Typography variant="h6" component="h2">
                  AI Prompt Details
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 300,
                  overflow: 'auto',
                  border: 1,
                  borderColor: 'grey.200',
                }}
              >
                <Typography
                  component="pre"
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    margin: 0,
                    lineHeight: 1.5,
                  }}
                >
                  {aiResponse.metadata.prompt}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        <EnhancedReportDisplay
          recommendations={aiResponse}
          loading={aiResponseLoading}
          patientName={review.session?.user ?
            `${review.session.user.firstName || ''} ${review.session.user.lastName || ''}`.trim() ||
            review.session.user.email :
            'Patient'
          }
        />
      </Box>
    </Box>
  )
}
